<template>
    <LoadingSpinner v-if="isLoading" />
    <div v-else class="container">
        <div class="row justify-content-center">
            <div class="col-md-12 col-sm-12 col-xs-12 col-lg-10 col-xl-9 col-xxl-9 mt-4">
                <div v-if="hasContext" class="card_thing mb-4">
                    <h4 v-if="contextTitle">{{ contextTitle }}</h4>
                    <p v-if="contextMessage" class="text_sec mb-0">{{ contextMessage }}</p>
                </div>

                <div class="text-center mb-4">
                    <h2>{{ $t('subscribe.title') }}</h2>
                    <p class="text_sec">
                        {{ $t('subscribe.description') }}
                        <a href="/" class="text_sec" @click.prevent="useLocalMode"><u>{{ $t('subscribe.localFree') }}</u></a>
                    </p>
                </div>

                <div class="features_card card_thing mb-4">
                    <h4>{{ $t('subscribe.cloudIncludes') }}</h4>
                    <div class="features_list">
                        <div class="feature_item">
                            <i class="bi bi-cloud"></i>
                            <span>{{ $t('subscribe.featureLibrary') }}</span>
                        </div>
                        <div class="feature_item">
                            <i class="bi bi-cloud-upload"></i>
                            <span>{{ $t('subscribe.featureAgent') }}</span>
                        </div>
                        <div class="feature_item">
                            <i class="bi bi-database"></i>
                            <span>{{ $t('subscribe.featureStorage') }}</span>
                        </div>
                        <div class="feature_item">
                            <i class="bi bi-link-45deg"></i>
                            <span>{{ $t('subscribe.featureLinks') }}</span>
                        </div>
                        <div class="feature_item">
                            <i class="bi bi-soundwave"></i>
                            <span>{{ $t('subscribe.featureTools') }}</span>
                        </div>
                    </div>
                </div>

                <div class="plans_container">
                    <div
                        class="plan_card card_thing"
                        :class="{ plan_card_selected: selectedPlan === 'monthly' }"
                        @click="selectedPlan = 'monthly'"
                    >
                        <div class="plan_header">
                            <h3>{{ $t('subscribe.monthly') }}</h3>
                            <p class="text_sec">{{ $t('subscribe.monthlyDescription') }}</p>
                        </div>
                        <div class="plan_price">
                            <span class="price_amount">$4.99</span>
                            <span class="price_period text_sec">{{ $t('subscribe.perMonth') }}</span>
                        </div>
                        <div class="plan_total text_sec">
                            {{ $t('subscribe.billedMonthly') }}
                        </div>
                    </div>

                    <div
                        class="plan_card card_thing"
                        :class="{ plan_card_selected: selectedPlan === 'yearly' }"
                        @click="selectedPlan = 'yearly'"
                    >
                        <div class="plan_badge">
                            <span class="chip_thing">{{ $t('subscribe.saveTen') }}</span>
                        </div>
                        <div class="plan_header">
                            <h3>{{ $t('subscribe.yearly') }}</h3>
                            <p class="text_sec">{{ $t('subscribe.yearlyDescription') }}</p>
                        </div>
                        <div class="plan_price">
                            <span class="price_amount">$4.49</span>
                            <span class="price_period text_sec">{{ $t('subscribe.perMonth') }}</span>
                        </div>
                        <div class="plan_total text_sec">
                            {{ $t('subscribe.billedYearly') }}
                        </div>
                    </div>
                </div>

                <div class="text-center mt-4">
                    <div v-if="isLoggedIn" class="btn-new btn_subscribe" @click="openCheckout">
                        <i class="bi bi-credit-card"></i>
                        {{ isCheckoutLoading ? $t('subscribe.openingCheckout') : $t('subscribe.subscribePlan', { plan: selectedPlanLabel }) }}
                    </div>
                    <div v-else class="btn-new btn_subscribe" @click="goToLogin">
                        <i class="bi bi-box-arrow-in-right"></i>
                        {{ $t('subscribe.registerSubscribe') }}
                    </div>
                    <p v-if="checkoutError" class="text_sec mt-2">{{ checkoutError }}</p>
                </div>

                <div class="info_section mt-4">
                    <p class="text_sec text-center">
                        <i class="bi bi-shield-check"></i>
                        {{ $t('subscribe.securePayment') }}
                    </p>
                    <p class="text_sec text-center legal_links">
                        <a href="https://demoparty.app/privacy.html" target="_blank">{{ $t('subscribe.privacy') }}</a> •
                        <a href="https://demoparty.app/terms.html" target="_blank">{{ $t('subscribe.terms') }}</a> •
                        <a href="https://demoparty.app/refund.html" target="_blank">{{ $t('subscribe.refund') }}</a>
                    </p>
                </div>

                <div class="card_thing mt-4 custom_plan_section">
                    <p class="text_sec mb-0">
                        <i class="bi bi-chat-dots"></i>
                        {{ $t('subscribe.customPlan') }}
                        <a href="mailto:support@demoparty.app" class="support_link">support@demoparty.app</a>
                    </p>
                </div>

                <div class="spacer" style="height: 15rem;"></div>
                </div>
                </div>
                </div>
</template>

<script>
import storageService from '@/scripts/services/StorageService'
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue'

export default {
    name: "SubscriptibeComponent",
    components: { LoadingSpinner },
    props: {
        contextTitle: {
            type: String,
            default: ''
        },
        contextMessage: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isLoading: true,
            selectedPlan: 'monthly',
            isCheckoutLoading: false,
            checkoutError: null
        }
    },
    computed: {
        rUsername() {
            return storageService.state.username
        },
        rUser() {
            return storageService.state.user
        },
        rSubscription() {
            return storageService.state.subscription
        },
        isLoggedIn() {
            return Boolean(storageService.state.token || storageService.state.client?.token);
        },
        hasContext() {
            return Boolean(this.contextTitle || this.contextMessage)
        },
        selectedPlanLabel() {
            return this.selectedPlan === 'yearly' ? this.$t('subscribe.yearly') : this.$t('subscribe.monthly')
        }
    },
    async mounted() {
        window.document.title = "subscribe - demoparty"
        if (!storageService.state.dataLoaded) {
            await storageService.loadData()
        }
        this.isLoading = false
    },
    methods: {
        goToLogin() {
            this.$router.push('/login')
        },
        useLocalMode() {
            if (storageService.state.storageType !== 'local') {
                storageService.switchStorages()
            }
            this.$router.push('/')
        },
        async openCheckout() {
            if (!this.isLoggedIn || !this.rUser?.id) {
                this.goToLogin()
                return
            }
            if (this.isCheckoutLoading) return

            this.checkoutError = null
            this.isCheckoutLoading = true

            try {
                const checkout = await storageService.createSubscriptionCheckout(this.selectedPlan)
                if (checkout?.checkout_url) {
                    window.location.href = checkout.checkout_url
                    return
                }
                this.checkoutError = this.$t('subscribe.checkoutFailed')
            } catch (error) {
                console.error('Failed to create checkout:', error)
                this.checkoutError = this.$t('subscribe.checkoutFailed')
            } finally {
                this.isCheckoutLoading = false
            }
        }
    }
}
</script>

<style scoped>
.features_list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
}

.feature_item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.feature_item i {
    color: #a3a3a3;
    font-size: 1.1rem;
    width: 1.5rem;
    text-align: center;
}

.plans_container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

@media (max-width: 576px) {
    .plans_container {
        grid-template-columns: 1fr;
    }
}

.plan_card {
    cursor: pointer;
    transition: border-color 200ms ease, transform 200ms ease;
    position: relative;
}

.plan_card:hover {
    transform: translateY(-2px);
}

.plan_card_selected {
    border-color: rgba(212, 128, 3, 0.6);
}

.plan_badge {
    position: absolute;
    top: -0.75rem;
    right: 1rem;
}

.plan_header h3 {
    margin-bottom: 0.25rem;
}

.plan_header p {
    margin-bottom: 0;
    font-size: 0.875rem;
}

.plan_price {
    margin-top: 1rem;
}

.price_amount {
    font-size: 2rem;
    font-weight: 700;
}

.price_period {
    font-size: 1rem;
}

.plan_total {
    font-size: 0.875rem;
    margin-top: 0.5rem;
}

.btn_subscribe {
    padding: 0.75rem 2rem;
    font-size: 1rem;
}

.info_section i {
    margin-right: 0.5rem;
}

.custom_plan_section i {
    margin-right: 0.5rem;
}

.support_link {
    color: #e4e4e4;
    text-decoration: underline;
}

.support_link:hover {
    color: #ffffff;
}

.legal_links a {
    color: #a3a3a3;
    text-decoration: none;
    font-size: 0.875rem;
}

.legal_links a:hover {
    color: #ffffff;
    text-decoration: underline;
}
</style>
